import React, { useEffect, useState } from "react";
import { adminApi } from "./adminApi";

const usd = (n, dp = 2) => `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const num = (n) => Number(n).toLocaleString("en-US");

function StatCard({ label, value, sub, testid }) {
    return (
        <div data-testid={testid} className="border border-burgundy/15 bg-cream p-6">
            <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">{label}</div>
            <div className="mt-2 font-display text-4xl text-burgundy leading-none">{value}</div>
            {sub && <div className="mt-2 text-xs text-ink-muted">{sub}</div>}
        </div>
    );
}

function SectionHeading({ kicker, children }) {
    return (
        <div className="mt-12">
            <span className="text-[10px] tracking-[0.32em] uppercase text-gold-deep">{kicker}</span>
            <h2 className="mt-2 font-display text-3xl text-burgundy">{children}</h2>
        </div>
    );
}

export default function AdminCosts() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [aiModel, setAiModel] = useState(null);
    const [savingModel, setSavingModel] = useState(false);

    useEffect(() => {
        adminApi.costs().then(setData).catch(() => setError("Could not load cost data."));
        adminApi.aiModel().then(setAiModel).catch(() => {});
    }, []);

    const changeModel = async (value) => {
        const [provider, model] = value.split("|");
        setSavingModel(true);
        try {
            await adminApi.setAiModel(provider, model);
            setAiModel(prev => ({ ...prev, provider, model }));
        } catch { /* keep previous selection */ }
        setSavingModel(false);
    };

    if (error) return <div className="p-10 text-burgundy">{error}</div>;
    if (!data) return <div className="p-10 text-ink-muted">Loading…</div>;

    const { ai, emails, revenue, net } = data;
    const maxFeatureCost = Math.max(...ai.by_feature.map(f => f.cost), 0.0001);

    return (
        <div className="p-10 max-w-6xl" data-testid="admin-costs">
            <span className="text-[10px] tracking-[0.36em] uppercase text-gold-deep">Costs</span>
            <h1 className="mt-2 font-display text-5xl text-burgundy">
                <span className="italic">What it</span> costs
            </h1>

            {aiModel && (
                <div className="mt-6 border border-burgundy/15 bg-cream p-5 max-w-lg" data-testid="ai-model-card">
                    <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">AI model (coach, concierge, insights)</div>
                    <select
                        data-testid="ai-model-select"
                        value={`${aiModel.provider}|${aiModel.model}`}
                        onChange={e => changeModel(e.target.value)}
                        disabled={savingModel}
                        className="mt-2 w-full border border-burgundy/25 bg-cream px-3 py-2.5 text-sm text-ink"
                    >
                        {(aiModel.allowed || []).map(m => (
                            <option key={`${m.provider}|${m.model}`} value={`${m.provider}|${m.model}`}>{m.label}</option>
                        ))}
                    </select>
                    <div className="mt-1.5 text-[10px] text-ink-light">All models run on your Universal Key — switching takes effect immediately.</div>
                </div>
            )}

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard testid="cost-ai-total" label="AI spend" value={usd(ai.total_cost)} sub={`${num(ai.total_calls)} calls · estimated`} />
                <StatCard testid="cost-emails-total" label="Emails sent" value={num(emails.total)} sub={`${num(emails.this_month)} this month · free tier`} />
                <StatCard testid="cost-revenue-gross" label="Gross revenue" value={usd(revenue.gross, 0)} sub={`${revenue.paid_unlocks} paid × ${usd(revenue.unit_price, 0)} · mocked`} />
                <StatCard testid="cost-net" label="Net" value={usd(net)} sub="gross − AI spend" />
            </div>

            <SectionHeading kicker="AI usage">
                <span className="italic">Where the</span> tokens go
            </SectionHeading>
            <div className="mt-2 text-xs text-ink-muted">
                {ai.model} · {usd(ai.pricing.input_per_m, 0)}/M input · {usd(ai.pricing.output_per_m, 0)}/M output ·
                {" "}{num(ai.input_tokens)} in / {num(ai.output_tokens)} out (estimated from stored content)
            </div>
            <div className="mt-6 border border-burgundy/15 bg-cream">
                {ai.by_feature.length === 0 ? (
                    <div className="px-6 py-10 text-center text-ink-muted text-sm">No AI usage yet.</div>
                ) : ai.by_feature.map((f) => (
                    <div key={f.feature} data-testid={`cost-feature-${f.feature.toLowerCase().replace(/\s+/g, "-")}`}
                        className="flex items-center justify-between px-6 py-4 border-b last:border-b-0 border-burgundy/10">
                        <div className="flex-1">
                            <div className="font-display text-lg text-burgundy">{f.feature}</div>
                            <div className="text-[10px] tracking-[0.2em] uppercase text-ink-muted mt-1">
                                {num(f.calls)} calls · {num(f.input_tokens)} in / {num(f.output_tokens)} out
                            </div>
                            <div className="mt-2 h-1 w-full bg-burgundy/10">
                                <div className="h-full bg-gold" style={{ width: `${(f.cost / maxFeatureCost) * 100}%` }} />
                            </div>
                        </div>
                        <div className="ml-6 text-right font-display text-2xl text-burgundy">{usd(f.cost, 4)}</div>
                    </div>
                ))}
            </div>

            <SectionHeading kicker="Email delivery">
                <span className="italic">What we've</span> sent
            </SectionHeading>
            <div className="mt-2 text-xs text-ink-muted">
                Resend · {num(emails.this_month)} of {num(emails.monthly_free_limit)} free sends used this month · cost {usd(emails.cost)}
            </div>
            <div className="mt-6 border border-burgundy/15 bg-cream">
                {emails.by_type.map((e) => (
                    <div key={e.type} data-testid={`cost-email-${e.type.toLowerCase().replace(/\s+/g, "-")}`}
                        className="flex items-center justify-between px-6 py-4 border-b last:border-b-0 border-burgundy/10">
                        <div className="font-display text-lg text-burgundy">{e.type}</div>
                        <div className="text-right">
                            <span className="font-display text-2xl text-burgundy">{num(e.count)}</span>
                            <span className="ml-2 text-[10px] tracking-[0.28em] uppercase text-ink-muted">sent</span>
                        </div>
                    </div>
                ))}
            </div>

            <SectionHeading kicker="Revenue">
                <span className="italic">What's come</span> in
            </SectionHeading>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
                <StatCard testid="cost-paid-unlocks" label="Paid unlocks" value={num(revenue.paid_unlocks)} sub={`${revenue.paid_last_7d} in last 7d`} />
                <StatCard testid="cost-unit-price" label="Founding price" value={usd(revenue.unit_price, 0)} sub="per member" />
                <StatCard testid="cost-gross" label="Gross" value={usd(revenue.gross, 0)} sub="Mocked checkout — no real charges yet" />
            </div>
            <div className="mt-4 text-xs text-ink-muted">
                Checkout is currently mocked. Figures become real once Stripe webhooks are connected.
            </div>
        </div>
    );
}
